import React, { useState, useEffect, useContext } from "react"
import { Link } from "react-router-dom"
import request from "request"

import { ApplicationContext } from "ApplicationContext"
import Header from "Header"
import AsyncContainer from "AsyncContainer"

const INTEGRATIONS = [
  {
    key: "vivenu",
    name: "Vivenu",
    logo: "/images/integrations/vivenu_dark.png",
    description: "Import food & drink vouchers sold through Vivenu ticketing as Prepaid Credits, so fans can redeem them at your concessions.",
    path: "/integrations/vivenu",
  },
  {
    key: "app8",
    name: "App8",
    logo: "/images/integrations/app8.svg",
    description: "Connect App8's mobile ordering & payment platform to sync in-venue orders and loyalty data with Catch.",
    path: "/integrations/app8",
  },
]

const SYNC_STATUS_COLORS = {
  success: "is-success",
  failed:  "is-danger",
  running: "is-warning",
  pending: "is-info",
}

const IntegrationCard = ({ integration, liveData }) => {
  const isConfigured = !!liveData
  const statusColor  = liveData?.lastSyncStatus
    ? SYNC_STATUS_COLORS[liveData.lastSyncStatus]
    : null

  const cardContent = (
    <div className="card" style={{ height: "100%", position: "relative" }}>
      {integration.comingSoon && (
        <div style={{
          position: "absolute", top: 12, right: 12,
          zIndex: 1,
        }}>
          <span className="tag is-warning is-light">Coming Soon</span>
        </div>
      )}

      <div className="card-content">
        {/* Logo */}
        <div className="mb-4" style={{ height: 48, display: "flex", alignItems: "center" }}>
          <img
            src={integration.logo}
            alt={`${integration.name} logo`}
            style={{ maxHeight: 40, maxWidth: 140, objectFit: "contain" }}
          />
        </div>

        {/* Description */}
        <p className="is-size-6 has-text-grey mb-4">{integration.description}</p>

        {/* Status badges */}
        <div className="tags">
          {isConfigured ? (
            <>
              <span className="tag is-success is-light">Connected</span>
              {statusColor && (
                <span className={`tag ${statusColor} is-light`}>
                  Last sync: {liveData.lastSyncStatus}
                </span>
              )}
              {liveData?.lastSyncedAt && (
                <span className="tag is-light">
                  {new Date(liveData.lastSyncedAt).toLocaleDateString()}
                </span>
              )}
            </>
          ) : (
            <span className="tag is-light">Not connected</span>
          )}
        </div>
      </div>

      <div className="card-footer">
        <div className="card-footer-item">
          {integration.comingSoon ? (
            <span className="has-text-grey is-size-7">Available soon</span>
          ) : (
            <span className="has-text-link is-size-6 has-text-weight-medium">
              {isConfigured ? "Manage →" : "Set up →"}
            </span>
          )}
        </div>
      </div>
    </div>
  )

  if (integration.comingSoon) {
    return <div style={{ cursor: "default", height: "100%", opacity: 0.75 }}>{cardContent}</div>
  }

  return (
    <Link to={integration.path} style={{ display: "block", height: "100%", textDecoration: "none", color: "inherit" }}>
      {cardContent}
    </Link>
  )
}

const IntegrationsView = () => {
  const { CurrentUserContext } = useContext(ApplicationContext)
  const { venue } = CurrentUserContext
  const [isLoaded, setIsLoaded]   = useState(false)
  const [liveMap, setLiveMap]     = useState({})  // provider → integration object

  useEffect(() => {
    let mounted = true
    Promise.all([
      request.get("/venue_admin/integrations"),
      request.get("/venue_admin/integrations/app8_stats"),
    ]).then(([intResp, app8Resp]) => {
      if (!mounted) return
      const map = {}
      ;(intResp.data.integrations || []).forEach(i => { map[i.provider] = i })

      const app8 = app8Resp.data.app8
      if (app8 && app8.totalInflows > 0) {
        map["app8"] = {
          provider: "app8",
          active: true,
          lastSyncedAt: app8.lastInflowAt,
          lastSyncStatus: "success",
          totalInflows: app8.totalInflows,
        }
      }

      setLiveMap(map)
      setIsLoaded(true)
    }).catch(() => mounted && setIsLoaded(true))
    return () => { mounted = false }
  }, [venue])

  return (
    <div>
      <Header text="Integrations" />

      <div className="block content">
        <p>Connect third-party platforms to automate data imports and keep your venue in sync.</p>
      </div>

      <AsyncContainer isLoaded={isLoaded}>
        <div className="columns is-multiline">
          {INTEGRATIONS.map(integration => (
            <div key={integration.key} className="column is-one-third">
              <IntegrationCard
                integration={integration}
                liveData={liveMap[integration.key]}
              />
            </div>
          ))}
        </div>
      </AsyncContainer>
    </div>
  )
}

export default IntegrationsView
